// src/components/SetupWizard/WizardStep4_DocumentUpload.js
//
// Phase 3D — Task 29: Optional Step 4 in onboarding wizard.
//
// Framing: additive context enrichment only — NOT a required setup step.
// Visually distinct from Step 2 (financials): no tier badge, no downstream
// consequences. Just a lightweight "optionally enrich your client's context."
//
// Props:
//   clientId       — string
//   clientData     — client document data
//   user           — Firebase auth user
//   onStepComplete — callback (called on both Skip and after successful upload)
//   onBack         — callback to go back to Step 3

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, Alert, LinearProgress,
  Chip, CircularProgress, Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  SkipNext as SkipIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import {
  collection, doc, setDoc, serverTimestamp, getDocs, query, where
} from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage, appCheck } from '../../firebase';
import { getToken } from 'firebase/app-check';
import { toast } from 'react-toastify';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function WizardStep4_DocumentUpload({
  clientId,
  clientData,
  user,
  onStepComplete,
  onBack,
}) {
    const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [activeDocCount, setActiveDocCount] = useState(0);
  const fileInputRef = useRef(null);

    const clientName = clientData?.name || clientData?.companyName || 'this client';
  const atMaxActive = activeDocCount >= 10;

  useEffect(() => {
    if (!clientId) return;

    const loadActiveDocCount = async () => {
      try {
        const q = query(
          collection(db, 'clientDocumentNarratives'),
          where('clientId', '==', clientId),
          where('isDeleted', '==', false)
        );
        const snap = await getDocs(q);
        setActiveDocCount(snap.size);
      } catch (err) {
        console.error('[WizardStep4] Failed to load active document count:', err);
      }
    };

    loadActiveDocCount();
  }, [clientId]);

  // ─── File validation ─────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File exceeds 10 MB limit.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error('Only PDF, DOCX, or TXT files are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  // ─── Upload ───────────────────────────────────────────────────────────────
    const handleUpload = useCallback(async () => {
    if (!selectedFile || !clientId || !user?.uid) return;

    if (atMaxActive) {
      toast.error('Maximum of 10 documents reached. Delete a document before uploading another.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Pre-create Firestore doc BEFORE uploading to Storage
    let newDocRef;
    try {
      newDocRef = doc(collection(db, 'clientDocumentNarratives'));
      await setDoc(newDocRef, {
        extractionStatus: 'processing',
        isDeleted: false,
        lastProcessingEventId: null,
        clientId,
        fcfoId: user.uid,
        fileName: selectedFile.name,
        originalFileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        uploadedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        isPinned: false,
        narrative: null,
      });
    } catch (err) {
      console.error('[WizardStep4] Pre-create failed:', err);
      toast.error('Failed to initialize upload. Please try again.');
      setUploading(false);
      return;
    }

     // Ensure App Check token is ready before upload (prevents 403 on enforced Storage)
    if (appCheck) {
      try {
        await getToken(appCheck);
      } catch (err) {
        console.warn('[WizardStep4] App Check token prefetch failed, proceeding anyway:', err.message);
      }
    }

    const storagePath = `clientDocuments/${clientId}/${newDocRef.id}/${selectedFile.name}`;
    const storageRefInstance = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRefInstance, selectedFile);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      },
      async (err) => {
        console.error('[WizardStep4] Upload error:', err);
        toast.error('Upload failed. You can add documents later from the Documents tab.');
        try {
          const { deleteDoc } = await import('firebase/firestore');
          await deleteDoc(newDocRef);
        } catch (cleanupErr) { /* non-blocking */ }
        setUploading(false);
        setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSelectedFile(null);
      },
      () => {
        // Upload complete — Cloud Function takes over for processing
        setUploading(false);
        setUploadProgress(0);
        setUploadComplete(true);
      }
    );
    }, [selectedFile, clientId, user, atMaxActive]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Step framing — visually distinct from Step 2 financials */}
      <Box sx={{
        p: 2, bgcolor: 'info.50', borderRadius: 1,
        border: '1px solid', borderColor: 'info.200',
        display: 'flex', alignItems: 'flex-start', gap: 1.5
      }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="info.dark">
            Optional — Add Context Document
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Upload a document to give SavvaiEdge additional context about {clientName} —
            a strategic plan, board deck, market analysis, or any relevant reference material.
            The platform will synthesize it into a structured narrative that enriches all future
            Q&amp;A answers and SavvaiSignals reports.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            You can skip this step and upload documents anytime from the client's Documents tab.
          </Typography>
        </Box>
      </Box>

      {/* What this is NOT — keep it distinct from Step 2 */}
      <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
        <Typography variant="caption">
          <strong>This is not a financial upload.</strong> Financial documents are handled in Step 2
          and feed directly into financial intelligence tiers. This step is for any other relevant
          reference material.
        </Typography>
      </Alert>

      {/* Upload area */}
            {!uploadComplete ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          {atMaxActive && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Maximum of 10 documents reached for this client. You can upload more after deleting an existing document from the Documents tab.
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading || atMaxActive}
            >
              Choose File
              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={handleFileChange}
              />
            </Button>

            {selectedFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.name}
                </Typography>
                <Chip
                  label={formatFileSize(selectedFile.size)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}

                        {selectedFile && !uploading && (
              <Button
                variant="contained"
                onClick={handleUpload}
                startIcon={<UploadIcon />}
                disabled={atMaxActive}
              >
                Upload
              </Button>
            )}
          </Box>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {uploadProgress < 100 ? `${Math.round(uploadProgress)}% uploaded` : 'Preparing for AI governance scan...'}
              </Typography>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Accepted: PDF, DOCX, or TXT · Max size: 10 MB
          </Typography>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: 'success.50', borderColor: 'success.300'
          }}
        >
          <CheckIcon color="success" />
          <Box>
            <Typography variant="body2" fontWeight={600} color="success.dark">
              Document uploaded successfully
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SavvaiEdge is processing your document in the background. Content will be scanned for sensitive information (PII) before AI analysis. The AI summary will be ready shortly.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
        <Button
          onClick={onBack}
          startIcon={<BackIcon />}
          variant="text"
          disabled={uploading}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Skip always available */}
          {!uploadComplete && (
            <Button
              onClick={() => onStepComplete({})}
              startIcon={<SkipIcon />}
              variant="outlined"
              color="inherit"
              disabled={uploading}
            >
              Skip for now
            </Button>
          )}

          {/* Continue after upload */}
                    {uploadComplete && (
            <Button
              variant="contained"
              onClick={() => onStepComplete({})}
            >
              Continue
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}